/**
 * /app/reservas — board del día + configuración de turnos.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarCheck,
  RefreshCw,
  Settings2,
  Users,
  Check,
  X,
  Armchair,
} from "lucide-react";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { ReservationServicesSettings } from "@/components/reservas/ReservationServicesSettings";
import { ManualReservationForm } from "@/components/reservas/ManualReservationForm";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { localCalendarDateIso } from "@/lib/format-date";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/reservas")({
  component: ReservasPage,
  validateSearch: (s: Record<string, unknown>) => ({
    config: s.config === true || s.config === "1" || s.config === 1 ? true : undefined,
    date: typeof s.date === "string" ? s.date : undefined,
  }),
  head: () => ({ meta: [{ title: "Reservas" }] }),
});

type ReservationStatus =
  | "confirmed"
  | "seated"
  | "completed"
  | "cancelled"
  | "no_show";

type Reservation = {
  id: string;
  reservedDate: string;
  reservedTime: string;
  partySize: number;
  guestName: string;
  guestPhone: string | null;
  notes: string | null;
  status: ReservationStatus;
  source: string;
  serviceNombre?: string;
  tableNombre?: string;
  tableId: string | null;
  sectorNombre?: string;
};

type TableOpt = { id: string; nombre: string; capacidad: number };

const STATUS_LABEL: Record<ReservationStatus, string> = {
  confirmed: "Confirmada",
  seated: "Sentada",
  completed: "Finalizada",
  cancelled: "Cancelada",
  no_show: "No-show",
};

const STATUS_STYLE: Record<ReservationStatus, string> = {
  confirmed: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  seated: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  completed: "border-border/50 bg-muted/40 text-muted-foreground",
  cancelled: "border-red-500/30 bg-red-500/10 text-red-300",
  no_show: "border-amber-500/30 bg-amber-500/10 text-amber-300",
};

function todayIso() {
  return localCalendarDateIso();
}

function ReservasPage() {
  const tid = useWorkspaceTenantId();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const configMode = Boolean(search.config);
  const date = search.date || todayIso();

  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState<PlanApiErrorPayload | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [tables, setTables] = useState<TableOpt[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Otras fechas con reservas (para no “perder” turnos web de días distintos). */
  const [otherDates, setOtherDates] = useState<{ date: string; count: number }[]>([]);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    setLocked(null);
    try {
      const [list, meta, all] = await Promise.all([
        apiGetJson(`/api/reservations?date=${encodeURIComponent(date)}`, tid) as Promise<{
          reservations?: Reservation[];
          _lock?: PlanApiErrorPayload;
        }>,
        apiGetJson(`/api/reservations?action=meta`, tid) as Promise<{
          tables?: TableOpt[];
          _lock?: PlanApiErrorPayload;
        }>,
        apiGetJson(`/api/reservations`, tid) as Promise<{
          reservations?: Reservation[];
          _lock?: PlanApiErrorPayload;
        }>,
      ]);
      if (list._lock || meta._lock || all._lock) {
        setLocked(list._lock ?? meta._lock ?? all._lock ?? null);
        return;
      }
      setReservations(list.reservations ?? []);
      setTables(meta.tables ?? []);
      const counts = new Map<string, number>();
      for (const r of all.reservations ?? []) {
        if (r.status === "cancelled") continue;
        if (!r.reservedDate || r.reservedDate === date) continue;
        counts.set(r.reservedDate, (counts.get(r.reservedDate) ?? 0) + 1);
      }
      setOtherDates(
        [...counts.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(0, 12)
          .map(([d, count]) => ({ date: d, count })),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudieron cargar las reservas");
    } finally {
      setLoading(false);
    }
  }, [tid, date]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchStatus = async (
    id: string,
    status: ReservationStatus,
    extra?: { tableId?: string | null },
  ) => {
    if (!tid) return;
    setBusyId(id);
    try {
      await apiSendJson(
        `/api/reservations?id=${encodeURIComponent(id)}`,
        "PATCH",
        { status, ...extra },
        tid,
      );
      toast.success(STATUS_LABEL[status]);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo actualizar");
    } finally {
      setBusyId(null);
    }
  };

  if (locked) {
    return (
      <ClinicalPageShell>
        <FeatureLockedState payload={locked} />
      </ClinicalPageShell>
    );
  }

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="reservas" />
      <CrudShellHeader
        title="Reservas"
        description={
          configMode
            ? "Definí los turnos del local: horarios, días y duración en mesa."
            : "Agenda del día — asignación de mesa al llegar."
        }
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => void navigate({ to: "/app/reservas", search: { config: configMode ? undefined : true, date } })}>
              <Settings2 className="h-4 w-4" />
              {configMode ? "Ver reservas" : "Configurar turnos"}
            </Button>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => void load()}>
              <RefreshCw className="h-4 w-4" /> Actualizar
            </Button>
          </div>
        }
      />

      {configMode ? (
        tid ? (
          <ReservationServicesSettings tenantId={tid} onChanged={load} />
        ) : (
          <Loading />
        )
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="res-date">Fecha</Label>
              <Input
                id="res-date"
                type="date"
                value={date}
                className="h-11 w-[11rem]"
                onChange={(e) =>
                  void navigate({
                    to: "/app/reservas",
                    search: { config: undefined, date: e.target.value || todayIso() },
                  })
                }
              />
            </div>
            <p className="pb-2 text-xs text-muted-foreground">
              {reservations.filter((r) => r.status === "confirmed" || r.status === "seated").length}{" "}
              activas · {reservations.reduce((a, r) => a + (r.status === "cancelled" || r.status === "no_show" ? 0 : r.partySize), 0)} covers
            </p>
          </div>

          {otherDates.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Otras fechas con reservas:</span>
              {otherDates.map((d) => (
                <Button
                  key={d.date}
                  type="button"
                  size="sm"
                  variant={d.date === date ? "default" : "outline"}
                  className="h-8"
                  onClick={() =>
                    void navigate({
                      to: "/app/reservas",
                      search: { config: undefined, date: d.date },
                    })
                  }
                >
                  {d.date.slice(8, 10)}/{d.date.slice(5, 7)} · {d.count}
                </Button>
              ))}
            </div>
          ) : null}

          {tid ? <ManualReservationForm tenantId={tid} date={date} onCreated={load} /> : null}

          {loading ? (
            <Loading />
          ) : reservations.length === 0 ? (
            <CrudEmptyState
              icon={<CalendarCheck className="h-8 w-8" />}
              title="Sin reservas este día"
              description={otherDates.length ? "Hay reservas en otras fechas — usá los chips de arriba o el selector de fecha." : "Cuando haya turnos activos, los clientes pueden reservar desde /market/:slug/reservar."}
              action={
                <Button
                  variant="outline"
                  onClick={() =>
                    void navigate({ to: "/app/reservas", search: { config: true, date } })
                  }
                >
                  Configurar turnos
                </Button>
              }
            />
          ) : (
            <ul className="space-y-2">
              {reservations.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-3 rounded-xl border border-border/50 bg-card/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-foreground">
                        {r.reservedTime.slice(0, 5)}
                      </span>
                      <span
                        className={cn(
                          "rounded-md border px-2 py-0.5 text-[11px] font-medium",
                          STATUS_STYLE[r.status],
                        )}
                      >
                        {STATUS_LABEL[r.status]}
                      </span>
                      {r.serviceNombre ? (
                        <span className="text-xs text-muted-foreground">{r.serviceNombre}</span>
                      ) : null}
                      {r.source && r.source !== "web" ? (
                        <span className="rounded-md border border-border/50 px-2 py-0.5 text-[11px] text-muted-foreground">
                          {r.source === "phone"
                            ? "Tel / WA"
                            : r.source === "walk_in"
                              ? "Walk-in"
                              : "Manual"}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-foreground">
                      {r.guestName}
                      <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        {r.partySize}
                      </span>
                      {r.guestPhone ? (
                        <span className="ml-2 text-xs text-muted-foreground">{r.guestPhone}</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {r.tableNombre ? `Mesa ${r.tableNombre}` : "Sin mesa asignada"}
                      {r.sectorNombre ? ` · ${r.sectorNombre}` : ""}
                      {r.notes ? ` · ${r.notes}` : ""}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {(r.status === "confirmed" || r.status === "seated") && (
                      <Select
                        value={r.tableId || "none"}
                        onValueChange={(v) =>
                          void patchStatus(r.id, r.status === "confirmed" ? "seated" : r.status, {
                            tableId: v === "none" ? null : v,
                          })
                        }
                      >
                        <SelectTrigger className="h-9 w-[9.5rem]">
                          <SelectValue placeholder="Mesa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin mesa</SelectItem>
                          {tables.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.nombre} ({t.capacidad})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {r.status === "confirmed" ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 gap-1"
                          disabled={busyId === r.id}
                          onClick={() => void patchStatus(r.id, "seated")}
                        >
                          <Armchair className="h-3.5 w-3.5" /> Sentar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-9 gap-1 text-amber-300"
                          disabled={busyId === r.id}
                          onClick={() => void patchStatus(r.id, "no_show")}
                        >
                          No-show
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-9 w-9 text-red-300"
                          disabled={busyId === r.id}
                          onClick={() => void patchStatus(r.id, "cancelled")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : null}
                    {r.status === "seated" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 gap-1"
                        disabled={busyId === r.id}
                        onClick={() => void patchStatus(r.id, "completed")}
                      >
                        <Check className="h-3.5 w-3.5" /> Finalizar
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </ClinicalPageShell>
  );
}
